import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/phoneModels";

const NAV_LINKS = [
    { to: "/designer", label: "Case Designer" },
    { to: "/phone-case-vending", label: "Phone Case Machines" },
    { to: "/food-drink-vending", label: "Food & Drink" },
    { to: "/host-a-machine", label: "Host a Machine" },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Close mobile nav on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header className="border-b border-neutral-200 bg-white/85 backdrop-blur sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-3 group" data-testid="nav-home-link">
                    <img
                        src={LOGO_URL}
                        alt="Vendagain logo"
                        className="h-9 w-9 rounded-md object-contain transition-transform group-hover:rotate-[8deg]"
                    />
                    <div className="leading-tight">
                        <div className="text-[15px] font-semibold tracking-tight">
                            Vendagain
                        </div>
                        <div className="text-[10px] text-neutral-500 font-medium uppercase tracking-[0.14em] hidden sm:block">
                            Perth Vending Solutions
                        </div>
                    </div>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            className={({ isActive }) =>
                                `px-3 py-2 text-sm rounded-full font-medium transition-colors ${
                                    isActive
                                        ? "bg-neutral-950 text-white"
                                        : "text-neutral-700 hover:text-neutral-950 hover:bg-neutral-100"
                                }`
                            }
                            data-testid={`nav-link-${l.to.replace(/\//g, "")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <Link
                        to="/host-a-machine#form"
                        className="hidden lg:inline-flex vg-pill vg-pill-accent text-xs"
                        data-testid="nav-cta"
                    >
                        Get a free machine
                    </Link>
                    <button
                        className="lg:hidden p-2 -mr-2 text-neutral-800"
                        onClick={() => setOpen(!open)}
                        aria-label="Menu"
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="nav-mobile-menu">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 space-y-1">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                className={({ isActive }) =>
                                    `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                                        isActive
                                            ? "bg-neutral-950 text-white"
                                            : "text-neutral-800 hover:bg-neutral-100"
                                    }`
                                }
                                data-testid={`nav-mobile-link-${l.to.replace(/\//g, "")}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/host-a-machine#form"
                            className="vg-pill vg-pill-accent w-full justify-center mt-3"
                        >
                            Get a free machine
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
